import create, { GetState, SetState } from "zustand";
import {
	SeatLayout,
	SeatLayoutSortOrder,
	Slot,
	buildDefaultLayout,
	compactSlots,
	expandSlots,
	reconcileLayout,
	resizeSlots,
	dropOntoSeat,
	reflowSlots,
	appendEmptyRow,
	DEFAULT_COLUMNS,
	DEFAULT_CARD_WIDTH,
	defaultRows,
} from "@/components/SeatPlan/seatLayout";

export type { SeatLayoutSortOrder };

interface SeatPlanState {
	/** `null` while the seat order still follows the class-level student sort order. */
	layoutSortOrder: SeatLayoutSortOrder | null;
	/**
	 * Which classroom the current layout belongs to (set by `useClassroomDetailsQuery` once it has
	 * hydrated this store). This store is a global singleton and a revisited class renders from the
	 * React Query cache before its GetClass refetch lands, so seat plan views compare against their
	 * own classroom to avoid drawing the previously opened class's grid.
	 */
	hydratedClassroomStableId: string | null;
	slots: Slot[];
	columns: number;
	rows: number;
	cardWidth: number;
	hasUnsavedArrangement: boolean;
	savedSnapshot: SeatLayout | null;
	savedSortOrderSnapshot: SeatLayoutSortOrder | null;
	activeId: number | null;
	overId: number | null;
	pendingMoveIndex: number | null;
	selectedStudents: string[];
}

interface SeatPlanActions {
	hydrate: (saved: SeatLayout | null | undefined, rosterIds: string[]) => void;
	setViewportColumns: (columns: number) => void;
	setCardWidth: (cardWidth: number) => void;
	setLayoutSortOrder: (layoutSortOrder: SeatLayoutSortOrder | null) => void;
	setHydratedClassroom: (classroomStableId: string | null) => void;
	rearrangeStudents: (studentIds: string[], sortOrder: "lastNameAscending") => SeatLayout;
	syncPinnedStudents: (rosterIds: string[], pinnedIds: string[]) => void;
	beginDrag: (index: number) => void;
	dragOver: (index: number | null) => void;
	endDrag: (from: number, to: number) => void;
	cancelDrag: () => void;
	pickUpSeat: (index: number) => void;
	cancelMove: () => void;
	discardArrangement: () => void;
	saveArrangement: () => SeatLayout;
	resetTransientArrangement: () => void;
	setSelection: (ids: string[]) => void;
	toggleSelection: (id: string) => void;
	clearSelection: () => void;
}

export type SeatPlanStore = SeatPlanState & SeatPlanActions;

export const INITIAL_SEAT_PLAN_STATE: SeatPlanState = {
	layoutSortOrder: "lastNameAscending",
	hydratedClassroomStableId: null,
	slots: [],
	columns: DEFAULT_COLUMNS,
	rows: defaultRows(0, DEFAULT_COLUMNS),
	cardWidth: DEFAULT_CARD_WIDTH,
	hasUnsavedArrangement: false,
	savedSnapshot: null,
	savedSortOrderSnapshot: null,
	activeId: null,
	overId: null,
	pendingMoveIndex: null,
	selectedStudents: [],
};

const snapshotLayout = (state: SeatPlanState): SeatLayout => ({
	slots: [...state.slots],
	columns: state.columns,
	rows: state.rows,
	cardWidth: state.cardWidth,
});

/** An auto-sorted grid holds no empty seats; only a hand-arranged layout has them. */
const isAutoSorted = (state: SeatPlanState): boolean => state.layoutSortOrder !== "custom";

/** Reveal the empty seats of the full grid so a drag can target any cell. */
const revealEmptySeats = (state: SeatPlanState) => (isAutoSorted(state) ? expandSlots(state.slots, state.columns, state.rows) : {});

/** Hide the empty seats again when a drag ends without rearranging anything. */
const hideEmptySeats = (state: SeatPlanState) => (isAutoSorted(state) ? compactSlots(state.slots, state.columns) : {});

const createActions = (set: SetState<SeatPlanStore>, get: GetState<SeatPlanStore>): SeatPlanActions => ({
	hydrate: (saved, rosterIds) => {
		const layout = saved ? reconcileLayout(saved, rosterIds) : buildDefaultLayout(rosterIds);
		// Roster changes (including pinning) re-run this reconcile with the grid as it stands, so an
		// auto-sorted grid must stay packed rather than being padded back out to columns x rows.
		const packed = saved && isAutoSorted(get()) ? compactSlots(layout.slots, layout.columns) : null;
		const slots = packed ? packed.slots : saved ? resizeSlots(layout.slots, layout.columns, layout.rows) : layout.slots;
		set({
			slots,
			columns: layout.columns,
			rows: packed ? packed.rows : layout.rows,
			cardWidth: layout.cardWidth,
			hasUnsavedArrangement: false,
			savedSnapshot: null,
			savedSortOrderSnapshot: null,
			activeId: null,
			overId: null,
			pendingMoveIndex: null,
			selectedStudents: [],
		});
	},

	setViewportColumns: (columns) => {
		const cols = Math.max(1, columns);
		const state = get();
		if (state.columns === cols) return;
		if (isAutoSorted(state)) {
			const compacted = compactSlots(state.slots, cols);
			const dragging = state.activeId !== null || state.pendingMoveIndex !== null;
			set({ columns: cols, ...(dragging ? expandSlots(compacted.slots, cols, compacted.rows) : compacted) });
			return;
		}
		const slots = reflowSlots(state.slots, cols);
		const rows = Math.max(1, Math.ceil(slots.length / cols) || 1);
		set({ columns: cols, rows, slots });
	},

	setCardWidth: (cardWidth) => set({ cardWidth }),
	setLayoutSortOrder: (layoutSortOrder) => set({ layoutSortOrder }),
	setHydratedClassroom: (classroomStableId) => set({ hydratedClassroomStableId: classroomStableId }),
	rearrangeStudents: (studentIds, sortOrder) => {
		const { columns, cardWidth } = get();
		const layout = { slots: [...studentIds], columns, rows: defaultRows(studentIds.length, columns), cardWidth };
		set({
			...layout,
			layoutSortOrder: sortOrder,
			pendingMoveIndex: null,
			selectedStudents: [],
			hasUnsavedArrangement: false,
			savedSnapshot: null,
			savedSortOrderSnapshot: null,
		});
		return layout;
	},

	syncPinnedStudents: (rosterIds, pinnedIds) => {
		const pinned = new Set(pinnedIds);
		const state = get();

		// An auto-sorted grid re-packs so a pinned student leaves no gap behind, and an unpinned
		// one returns to their place in the sort rather than to the first free seat. It also re-seats
		// on every activity change, so hold off mid-drag rather than moving seats under the tile
		// being dragged (the drag has also revealed empty seats that re-packing would remove).
		if (isAutoSorted(state)) {
			if (state.activeId !== null || state.pendingMoveIndex !== null) return;
			const seated: Slot[] = rosterIds.filter((id) => !pinned.has(id));
			const rows = defaultRows(seated.length, state.columns);
			const unchanged = seated.length === state.slots.length && seated.every((id, i) => id === state.slots[i]);
			if (unchanged && rows === state.rows) return;
			set({ slots: seated, rows });
			return;
		}

		const roster = new Set(rosterIds);
		let slots = [...state.slots];
		let rows = state.rows;

		// Hydrate is skipped while the arrangement is unsaved, so this is the path that must drop
		// students who left the class. Empty seats stay empty.
		slots = slots.map((id) => (id && (pinned.has(id) || !roster.has(id)) ? null : id));

		const placed = new Set(slots.filter((s): s is string => s !== null));
		const toPlace = rosterIds.filter((id) => !pinned.has(id) && !placed.has(id));

		for (const id of toPlace) {
			let emptyIndex = slots.indexOf(null);
			if (emptyIndex === -1) {
				slots = appendEmptyRow(slots, state.columns);
				rows += 1;
				emptyIndex = slots.indexOf(null);
			}
			slots[emptyIndex] = id;
			placed.add(id);
		}

		const occupied = slots.filter((s): s is string => s !== null).length;
		rows = Math.max(rows, defaultRows(occupied, state.columns), Math.ceil(slots.length / state.columns) || 1);
		slots = resizeSlots(slots, state.columns, rows);
		const sameSlots = slots.length === state.slots.length && slots.every((s, i) => s === state.slots[i]);
		if (sameSlots && rows === state.rows) return;
		set({ slots, rows });
	},

	beginDrag: (index) => {
		const state = get();
		if (state.hasUnsavedArrangement) {
			set({ activeId: index });
			return;
		}
		set({
			activeId: index,
			...revealEmptySeats(state),
			savedSnapshot: snapshotLayout(state),
			savedSortOrderSnapshot: state.layoutSortOrder,
		});
	},
	dragOver: (index) => set({ overId: index }),
	endDrag: (from, to) => {
		const state = get();
		const clearDrag = () => {
			if (state.hasUnsavedArrangement) {
				set({ activeId: null, overId: null, pendingMoveIndex: null });
			} else {
				set({
					activeId: null,
					overId: null,
					pendingMoveIndex: null,
					...hideEmptySeats(state),
					savedSnapshot: null,
					savedSortOrderSnapshot: null,
				});
			}
		};
		if (from === to && to < state.slots.length) {
			clearDrag();
			return;
		}
		const dropped = dropOntoSeat(state.slots, state.columns, from, to);
		const unchanged = dropped.slots.length === state.slots.length && dropped.slots.every((s, i) => s === state.slots[i]);
		if (unchanged) {
			clearDrag();
			return;
		}
		if (state.hasUnsavedArrangement) {
			set({
				slots: dropped.slots,
				rows: dropped.rows,
				activeId: null,
				overId: null,
				pendingMoveIndex: null,
				layoutSortOrder: "custom",
				hasUnsavedArrangement: true,
			});
			return;
		}
		// beginDrag / pickUpSeat already captured the layout as it stood before the drag, including
		// the empty seats an auto-sorted grid keeps hidden, so that snapshot is the one to discard to.
		const snapshot = state.savedSnapshot
			? { savedSnapshot: state.savedSnapshot, savedSortOrderSnapshot: state.savedSortOrderSnapshot }
			: { savedSnapshot: snapshotLayout(state), savedSortOrderSnapshot: state.layoutSortOrder };
		set({
			slots: dropped.slots,
			rows: dropped.rows,
			activeId: null,
			overId: null,
			pendingMoveIndex: null,
			layoutSortOrder: "custom",
			hasUnsavedArrangement: true,
			// Seat checkboxes go inert while the arrangement is unsaved, so drop any selection made
			// before the move rather than leaving a bulk action armed that can no longer be changed.
			selectedStudents: [],
			...snapshot,
		});
	},
	cancelDrag: () => {
		const state = get();
		if (state.hasUnsavedArrangement) {
			set({ activeId: null, overId: null });
			return;
		}
		set({ activeId: null, overId: null, ...hideEmptySeats(state), savedSnapshot: null, savedSortOrderSnapshot: null });
	},
	pickUpSeat: (index) => {
		const state = get();
		if (state.hasUnsavedArrangement) {
			set({ pendingMoveIndex: index });
			return;
		}
		set({
			pendingMoveIndex: index,
			...revealEmptySeats(state),
			savedSnapshot: snapshotLayout(state),
			savedSortOrderSnapshot: state.layoutSortOrder,
		});
	},
	cancelMove: () => {
		const state = get();
		if (state.hasUnsavedArrangement) {
			set({ pendingMoveIndex: null });
			return;
		}
		set({ pendingMoveIndex: null, ...hideEmptySeats(state), savedSnapshot: null, savedSortOrderSnapshot: null });
	},

	discardArrangement: () => {
		const snap = get().savedSnapshot;
		const sortOrderSnap = get().savedSortOrderSnapshot;
		if (snap) {
			set({
				slots: snap.slots,
				columns: snap.columns,
				rows: snap.rows,
				cardWidth: snap.cardWidth,
				layoutSortOrder: sortOrderSnap,
			});
		}
		set({
			hasUnsavedArrangement: false,
			savedSnapshot: null,
			savedSortOrderSnapshot: null,
			pendingMoveIndex: null,
			activeId: null,
			overId: null,
		});
	},

	saveArrangement: () => {
		const { slots, columns, rows, cardWidth } = get();
		set({
			hasUnsavedArrangement: false,
			savedSnapshot: null,
			savedSortOrderSnapshot: null,
			pendingMoveIndex: null,
			layoutSortOrder: "custom",
		});
		return { slots, columns, rows, cardWidth };
	},

	resetTransientArrangement: () => {
		const { hasUnsavedArrangement } = get();
		if (hasUnsavedArrangement) {
			get().discardArrangement();
		} else {
			const state = get();
			set({
				pendingMoveIndex: null,
				activeId: null,
				overId: null,
				...hideEmptySeats(state),
				savedSnapshot: null,
				savedSortOrderSnapshot: null,
			});
		}
	},

	setSelection: (ids) => set({ selectedStudents: ids }),
	toggleSelection: (id) =>
		set((state) => ({
			selectedStudents: state.selectedStudents.includes(id)
				? state.selectedStudents.filter((s) => s !== id)
				: [...state.selectedStudents, id],
		})),
	clearSelection: () => set({ selectedStudents: [] }),
});

export const useSeatPlanStore = create<SeatPlanStore>((set, get) => ({
	...INITIAL_SEAT_PLAN_STATE,
	...createActions(set, get),
}));
